import {
	useGetWorkspaceBudgetSettings,
	useSetWorkspaceBudgetSettings,
} from '@repo/api-codegen';
import { useCallback } from 'react';
import { openModal } from '../../../components/ModalManager';
import { EditBudgetSettingsModalController } from '../../EditBudgetSettingsModal';
import { BUDGET_DEFAULTS } from '../constants';

interface UseBudgetSettingsProps {
	onSettingsChange?: () => void;
}

export function useBudgetSettings({
	onSettingsChange,
}: UseBudgetSettingsProps) {
	const { data: budgetSettings, refetch: refetchBudgetSettings } =
		useGetWorkspaceBudgetSettings({});
	const { mutate: setBudgetSettings } = useSetWorkspaceBudgetSettings({
		onSuccess: () => {
			refetchBudgetSettings();
			onSettingsChange?.();
		},
	});

	const handleEditBudgetSettings = useCallback(() => {
		openModal({
			title: 'Spend limit settings',
			children: (
				<EditBudgetSettingsModalController onSuccess={onSettingsChange} />
			),
		});
	}, [onSettingsChange]);

	const handleToggleBudgetEnabled = useCallback(
		(enabled: boolean) => {
			setBudgetSettings({
				body: {
					monthly_budget:
						budgetSettings?.monthly_budget || BUDGET_DEFAULTS.MONTHLY_BUDGET,
					enabled,
				},
			});
		},
		[budgetSettings?.monthly_budget, setBudgetSettings]
	);

	const formatCurrency = (amount: number) =>
		new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(amount);

	// Calculate progress percentage
	const monthlyBudget = budgetSettings?.monthly_budget || 0;
	const spendMonthToDate = budgetSettings?.credit_spend_month_to_date || 0;
	const progressPercentage =
		monthlyBudget > 0 ? (spendMonthToDate / monthlyBudget) * 100 : 0;

	// Determine progress bar color based on usage
	const getProgressColor = (percentage: number) => {
		if (percentage >= 90) return 'critical';
		if (percentage >= 75) return 'warning';
		return 'primary';
	};

	return {
		budgetSettings,
		refetchBudgetSettings,
		setBudgetSettings,
		handleEditBudgetSettings,
		handleToggleBudgetEnabled,
		formatCurrency,
		progressPercentage,
		getProgressColor,
	};
}
